import React, { useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import ActionToast from "../../../../shared/components/ActionToast";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";

interface OfferShareButtonsProps {
  offerNumber: string;
  customerName?: string;
}

const OfferShareButtons: React.FC<OfferShareButtonsProps> = ({ offerNumber, customerName }) => {
  const { translate } = useTranslation(["serviceOffers"]);
  const [snackOpen, setSnackOpen] = useState(false);

  const currentUrl = window.location.href;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setSnackOpen(true);
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = currentUrl;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setSnackOpen(true);
    }
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(
      `Offer ${offerNumber}${customerName ? ` - ${customerName}` : ""}`
    );
    const body = encodeURIComponent(
      `${translate("serviceOffers:offerDetail.shareEmailBody")}: ${currentUrl}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleWebShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Offer ${offerNumber}`,
          text: customerName ? `Offer for ${customerName}` : `Offer ${offerNumber}`,
          url: currentUrl,
        });
      } catch (err) {
        // User cancelled or share failed
      }
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const hasWebShare = typeof navigator.share === "function";

  const iconBtnSx = {
    color: dt.textMuted,
    padding: "6px",
    transition: "all 0.15s ease",
    "&:hover": {
      color: dt.blue,
      backgroundColor: dt.blueGlow,
    },
  };

  return (
    <>
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: "2px" }}>
        <Tooltip title={translate("serviceOffers:offerDetail.copyLink")} arrow>
          <IconButton size="small" onClick={handleCopyLink} sx={iconBtnSx}>
            <ContentCopyOutlinedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>

        <Tooltip title={translate("serviceOffers:offerDetail.shareEmail")} arrow>
          <IconButton size="small" onClick={handleEmailShare} sx={iconBtnSx}>
            <EmailOutlinedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>

        {hasWebShare && (
          <Tooltip title={translate("serviceOffers:offerDetail.share")} arrow>
            <IconButton size="small" onClick={handleWebShare} sx={iconBtnSx}>
              <IosShareOutlinedIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={translate("serviceOffers:offerDetail.print")} arrow>
          <IconButton size="small" onClick={handlePrint} sx={iconBtnSx}>
            <PrintOutlinedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <ActionToast
        open={snackOpen}
        variant="success"
        message={translate("serviceOffers:offerDetail.linkCopied")}
        onClose={() => setSnackOpen(false)}
        duration={2000}
      />
    </>
  );
};

export default OfferShareButtons;
